/**
 * Channel-aware product listing links for agent-side product cards.
 *
 * A product card rendered in a TikTok conversation must open the TikTok
 * listing — not Shopee's. Until 2026-07-25 every card tap resolved to
 * shopee.com.my regardless of the conversation's channel, so staff on a
 * TikTok/Lazada thread were sent to a different marketplace (owner report,
 * conv 23567: a TikTok card opened the Shopee PDP).
 *
 * Discrimination is by inbox NAME, not channel_type: Shopee, TikTok, Lazada
 * and the WhatsApp/Evolution inboxes are ALL `Channel::Api`, so channel_type
 * cannot tell them apart. Live inbox names (account 3):
 *   Shopee - MK | TikTok - MK | Lazada - MK | WS-5388-MK | WS-6855-MK
 *   Shopee - S3 | TikTok - S3 | Lazada - S3 | WS-3128-S3
 */

// `<Marketplace> - <COMPANY>` or `WS-<line>-<COMPANY>`.
const CHANNEL_PATTERNS = [
  [/^shopee\b/i, 'shopee'],
  [/^tiktok\b/i, 'tiktok'],
  [/^lazada\b/i, 'lazada'],
  [/^ws[-\s]/i, 'whatsapp'],
];

// makerhub.my is MakerHub's storefront only — never link an S3 (Smith3D)
// conversation there. Company rides in the inbox name suffix.
const isMakerHubInbox = name => /(^|[-\s])MK$/i.test((name || '').trim());

/**
 * Map a Chatwoot inbox to the marketplace it bridges.
 * @returns {'shopee'|'tiktok'|'lazada'|'whatsapp'|null}
 */
export const channelOfInbox = inbox => {
  if (!inbox) return null;
  if (inbox.channel_type === 'Channel::Whatsapp') return 'whatsapp';
  const name = (inbox.name || '').trim();
  const hit = CHANNEL_PATTERNS.find(([re]) => re.test(name));
  return hit ? hit[1] : null;
};

// Our own storefront. `/s/<SKU>` 30x's to the WooCommerce product page and is
// the canonical link the CS brain already shares on WhatsApp
// (mk-agent server-bridge.js, mk-wa-agent chatAiBridge.js).
const websiteUrl = card =>
  card.sku ? `https://makerhub.my/s/${card.sku}` : '';

// Per-channel listing URL built from the ids `/api/products/cards` returns
// (productsService.cardLookup → shopee_url, tiktok_id, lazada_id).
const CHANNEL_URL = {
  shopee: card =>
    card.shopee_url ||
    card.shopeeUrl ||
    (card.shopId && card.shopeeId
      ? `https://shopee.com.my/product/${card.shopId}/${card.shopeeId}`
      : ''),
  // Owner-specified form (2026-07-25). TikTok's own shared cards use the
  // shop-my.tiktok.com/view/product/<id> variant; both resolve to the PDP.
  tiktok: card =>
    card.tiktok_id ? `https://shop.tiktok.com/my/pdp/${card.tiktok_id}` : '',
  lazada: card =>
    card.lazada_id
      ? `https://www.lazada.com.my/products/i${card.lazada_id}.html`
      : '',
  whatsapp: websiteUrl,
};

/**
 * Best link for a product card in a given channel.
 *
 * Order: a URL the message itself carried (that IS the listing the customer is
 * looking at) → the channel's own listing → our website. A product not listed
 * on this channel falls back to our storefront rather than a foreign
 * marketplace or a dead search-by-SKU (owner 2026-06-12: "a search-by-SKU
 * fallback is NOT our store — always link the real listing").
 *
 * @param {object} card  card fields (sku, shopee_url, tiktok_id, lazada_id, …)
 * @param {string|null} channel  from channelOfInbox()
 * @param {object} [inbox]  used only to keep makerhub.my off S3 conversations
 */
export const productHref = (card, channel, inbox) => {
  if (!card) return '#';

  const carried = card.sourceUrl || card.url;
  if (carried) return carried;

  const build = CHANNEL_URL[channel];
  const listing = build ? build(card) : '';
  if (listing) return listing;

  // No listing on this channel (or an unmapped inbox — email/social).
  // Only MakerHub inboxes may fall back to makerhub.my.
  if (!inbox || isMakerHubInbox(inbox.name)) {
    const site = websiteUrl(card);
    if (site) return site;
  }
  return '#';
};

// Marketplace/storefront listing URLs a buyer-shared card may carry inline.
// Matches shop.tiktok.com and shop-my.tiktok.com, shope.ee short links, etc.
export const LISTING_URL_RE =
  /https:\/\/(?:[\w-]+\.)*(?:shopee\.com\.my|shope\.ee|tiktok\.com|lazada\.com\.my|makerhub\.my)\/[^\s]+/;
